import { useState, useRef, useEffect } from 'react';
import './CustomSelect.css';

export default function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  renderOption,
  renderSelected,
  disabled = false,
  required = false,
  label = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef(null);

  const selectedOption = options.find(opt =>
    (typeof opt === 'object' ? opt.id || opt.value : opt) === value
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          setIsOpen(false);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(i => Math.min(i + 1, options.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(i => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (options[highlightedIndex]) {
            const opt = options[highlightedIndex];
            onChange(typeof opt === 'object' ? opt.id || opt.value : opt);
            setIsOpen(false);
          }
          break;
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, highlightedIndex, options, onChange]);

  const handleSelect = (option) => {
    const val = typeof option === 'object' ? option.id || option.value : option;
    onChange(val);
    setIsOpen(false);
  };

  const getOptionLabel = (option) => {
    if (typeof option === 'object') {
      return option.name || option.label || option.id;
    }
    return option;
  };

  const getOptionValue = (option) => {
    if (typeof option === 'object') {
      return option.id || option.value;
    }
    return option;
  };

  return (
    <div
      ref={containerRef}
      className={`custom-select ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
    >
      {label && (
        <label className="custom-select-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      <div
        className="custom-select-trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === 'Enter' && !disabled && setIsOpen(!isOpen)}
      >
        <span className="custom-select-value">
          {selectedOption ? (
            renderSelected ? renderSelected(selectedOption) : getOptionLabel(selectedOption)
          ) : (
            <span className="placeholder">{placeholder}</span>
          )}
        </span>
        <span className="custom-select-arrow">▼</span>
      </div>

      {isOpen && (
        <div className="custom-select-dropdown">
          <div className="custom-select-options">
            {options.map((option, index) => (
              <div
                key={getOptionValue(option)}
                className={`custom-select-option ${
                  getOptionValue(option) === value ? 'selected' : ''
                } ${index === highlightedIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {renderOption ? renderOption(option) : getOptionLabel(option)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
